/**
 * Marktlage — Lesefunktionen mit ALTERSANGABE.
 *
 * Audit 08.08.2026 (Ronni: „ich hab da kein Vertrauen"). Der Befund gibt ihm
 * recht — es war ein echter Fehler, kein Bauchgefühl:
 *
 * `fetchLatestSentiment()` holte die NEUESTE Zeile aus `v5_sentiment_regime`,
 * ohne zu prüfen, ob darin überhaupt ein Angst/Gier-Wert steht. Die Tabelle
 * wird aber von ZWEI Quellen abwechselnd befüllt: der eigene Fear&Greed-Wert
 * und die AAII-Umfrage schreiben getrennte Zeilen, und jede lässt die Felder
 * der anderen leer:
 *
 *   2026-08-07  fg=—    aaii=20,9
 *   2026-08-06  fg=69   aaii=—
 *   2026-08-05  fg=67   aaii=—
 *   2026-08-03  fg=—    aaii=49,2
 *
 * An einem AAII-Tag fiel damit die am stärksten gewichtete Achse (Gewicht 2
 * von 4) komplett aus, ohne dass es irgendwo sichtbar wurde — das Wetter
 * wurde still nur noch aus VIX und Marktphase gerechnet. Beispiel: Bei
 * Fear&Greed 25 und VIX 15 ergibt die vollständige Rechnung „neutral" (46),
 * dieselbe Lage ohne die Stimmungs-Achse dagegen „Gier" (68). Das Urteil
 * sprang also je nachdem, welche Quelle zuletzt geschrieben hatte.
 *
 * Deshalb hier: jeder Wert wird mit seinem eigenen Datum geholt, und das
 * Alter wird mitgeliefert statt verschwiegen.
 */
import { supabase } from '@/lib/supabase';

export interface MesswertMitAlter {
  wert: number;
  /** Beobachtungsdatum (YYYY-MM-DD). */
  datum: string;
  /** Kalendertage seit der Beobachtung. */
  alter_tage: number;
}

function alterTage(datum: string): number {
  const d = Date.parse(`${datum}T00:00:00Z`);
  if (!Number.isFinite(d)) return 999;
  return Math.max(0, Math.floor((Date.now() - d) / 86_400_000));
}

/**
 * Angst/Gier — aus der letzten Zeile, die tatsächlich einen Wert hat.
 * Der frühere Fehler war genau das fehlende `.not(...)`.
 */
export async function fetchAngstGier(signal?: AbortSignal): Promise<MesswertMitAlter | null> {
  const { data, error } = await supabase
    .from('v5_sentiment_regime')
    .select('fg_index, observation_date')
    .not('fg_index', 'is', null)
    .order('observation_date', { ascending: false })
    .limit(1)
    .abortSignal(signal!);
  if (error || !data?.[0]) return null;
  const zeile = data[0] as { fg_index: number; observation_date: string };
  return {
    wert: Number(zeile.fg_index),
    datum: String(zeile.observation_date).slice(0, 10),
    alter_tage: alterTage(String(zeile.observation_date).slice(0, 10)),
  };
}

export async function fetchVix(signal?: AbortSignal): Promise<MesswertMitAlter | null> {
  const { data, error } = await supabase
    .from('v5_vix_history')
    .select('vix_close, as_of_date')
    .not('vix_close', 'is', null)
    .order('as_of_date', { ascending: false })
    .limit(1)
    .abortSignal(signal!);
  if (error || !data?.[0]) return null;
  const zeile = data[0] as { vix_close: number; as_of_date: string };
  return {
    wert: Number(zeile.vix_close),
    datum: String(zeile.as_of_date).slice(0, 10),
    alter_tage: alterTage(String(zeile.as_of_date).slice(0, 10)),
  };
}

export interface SpyVerlaufPunkt {
  datum: string;
  /** Abstand zum 52-Wochen-Hoch in Prozent (≤ 0). */
  dd52_pct: number;
}

/**
 * SPY-Abstand zum 52-Wochen-Hoch über die letzten ~12 Monate.
 *
 * Das ist die ehrlichste „Wetter"-Kennzahl für einen antizyklischen Trader:
 * Sie beruht auf Kursdaten (die einzige Quelle mit echter Historie), hat eine
 * echte Zeitachse und beantwortet die Frage, die zählt — steht der Gesamtmarkt
 * nahe seinem Hoch oder im Rücksetzer.
 */
export async function fetchSpyDrawdownVerlauf(signal?: AbortSignal): Promise<SpyVerlaufPunkt[]> {
  // 252 Bars für das 52-Wochen-Fenster + 252 für den Verlauf.
  const { data, error } = await supabase
    .from('v5_ohlcv_history')
    .select('bar_date, high, close')
    .eq('symbol', 'SPY')
    .eq('timeframe', 'daily')
    .order('bar_date', { ascending: false })
    .limit(520)
    .abortSignal(signal!);
  if (error || !data || data.length < 260) return [];

  const bars = (data as Array<{ bar_date: string; high: number; close: number }>)
    .map((r) => ({ datum: String(r.bar_date).slice(0, 10), high: Number(r.high), close: Number(r.close) }))
    .filter((b) => Number.isFinite(b.high) && Number.isFinite(b.close))
    .reverse(); // aufsteigend

  const out: SpyVerlaufPunkt[] = [];
  for (let i = 251; i < bars.length; i++) {
    let hoch = 0;
    for (let j = i - 251; j <= i; j++) if (bars[j]!.high > hoch) hoch = bars[j]!.high;
    if (hoch <= 0) continue;
    out.push({
      datum: bars[i]!.datum,
      dd52_pct: Math.round((bars[i]!.close / hoch - 1) * 1000) / 10,
    });
  }
  return out;
}
